import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RefreshIcon from "@mui/icons-material/Refresh";
import StopIcon from "@mui/icons-material/Stop";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { hostname } from "../utils";

// CSS for spinning animation
const spinAnimation = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

export interface Voice {
  id: string; // UUID primary key for database storage
  voiceId: string; // ElevenLabs voice ID for API calls
  voice_id?: string;
  name: string;
  description?: string;
  category?: string;
  gender?: string;
  accent?: string;
  age?: string;
  labels?: {
    gender?: string;
    accent?: string;
    age?: string;
  };
}

interface VoiceSelectorProps {
  value: string | null;
  onChange: (voiceId: string | null) => void;
  companyId?: string;
  getToken: () => Promise<string>;
  disabled?: boolean;
  label?: string;
  helperText?: string;
  showTestButton?: boolean;
  ttsModel?: string;
  eventListener?: (event: any) => void;
  sx?: any;
  /**
   * Which value to return and match against:
   * - "id": UUID primary key (for database FK relationships, default)
   * - "voiceId": ElevenLabs voice ID string (for legacy fields)
   */
  valueType?: "id" | "voiceId";
}

const VoiceSelector: React.FC<VoiceSelectorProps> = ({
  value,
  onChange,
  companyId,
  getToken,
  disabled = false,
  label = "Voice Selection",
  helperText,
  showTestButton = true,
  ttsModel = "eleven_flash_v2_5",
  eventListener,
  sx,
  valueType = "id",
}) => {
  const [voices, setVoices] = useState<Voice[]>([]);
  const [loading, setLoading] = useState(false);
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Inject spin animation style
  useEffect(() => {
    const styleId = "voice-selector-spin-animation";
    if (!document.getElementById(styleId)) {
      const styleElement = document.createElement("style");
      styleElement.id = styleId;
      styleElement.textContent = spinAnimation;
      document.head.appendChild(styleElement);
    }
  }, []);

  // Load voices
  const loadVoices = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const params: {
        companyId?: string;
        includePublic: boolean;
        pageSize: number;
      } = {
        includePublic: true,
        pageSize: 1000,
      };
      if (companyId) {
        params.companyId = companyId;
      }
      const response = await axios.get(`${hostname}/voices`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      // API returns { items: [...], total, page, pages, has_next }
      if (response.data.items) {
        setVoices(response.data.items);
      } else if (response.data.voices) {
        // Fallback for alternative API format
        setVoices(response.data.voices);
      }
    } catch (err) {
      console.error("Failed to load voices:", err);
      // Use fallback voices if API fails (these won't work with valueType="id")
      setVoices([
        {
          id: "fallback-adam",
          voiceId: "pNInz6obpgDQGcFmaJgB",
          name: "Adam",
          category: "Male, Deep",
        },
        {
          id: "fallback-rachel",
          voiceId: "JBFqnCBsd6RMkjVDRZzb",
          name: "Rachel",
          category: "Female, Professional",
        },
        {
          id: "fallback-sam",
          voiceId: "yoZ06aMxZJJ28mfd3POQ",
          name: "Sam",
          category: "Male, Young",
        },
      ] as Voice[]);
    } finally {
      setLoading(false);
    }
  }, [companyId, getToken]);

  useEffect(() => {
    loadVoices();
  }, [loadVoices]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const handleVoicePreview = async (
    voiceId: string,
    voiceName: string,
    event?: React.MouseEvent,
  ) => {
    // Prevent the autocomplete from selecting this option when clicking play
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    // If already playing this voice, stop it
    if (playingVoiceId === voiceId && audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setPlayingVoiceId(null);
      return;
    }

    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setPlayingVoiceId(null);
    }

    try {
      setPreviewLoading(voiceId);
      const token = await getToken();

      // Generate preview text
      const previewText = `Hello! This is ${voiceName}. I'm here to help you with any questions or tasks you might have.`;

      // Call backend to generate audio
      const response = await axios.post(
        `${hostname}/voice-ws/elevenlabs/preview`,
        {
          voiceId: voiceId,
          voiceName: voiceName,
          ttsModel: ttsModel,
          text: previewText,
          companyId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: "blob",
        },
      );

      // Create audio URL and play
      const audioBlob = new Blob([response.data], { type: "audio/mpeg" });
      const audioUrl = URL.createObjectURL(audioBlob);

      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      audio.onended = () => {
        setPlayingVoiceId(null);
        URL.revokeObjectURL(audioUrl);
        audioRef.current = null;
      };

      audio.onerror = () => {
        console.error("Error playing audio");
        setPlayingVoiceId(null);
        URL.revokeObjectURL(audioUrl);
        audioRef.current = null;
      };

      await audio.play();
      setPlayingVoiceId(voiceId);
    } catch (error) {
      console.error("Failed to preview voice:", error);
      if (eventListener) {
        eventListener({
          type: "SET_ERROR",
          error: "Failed to preview voice. Please try again.",
        });
      }
    } finally {
      setPreviewLoading(null);
    }
  };

  const getOptionLabel = (option: Voice) => {
    const labels = option.labels || {};
    const gender = labels.gender || option.gender || "";
    const accent = labels.accent || option.accent || "";
    const age = labels.age || option.age || "";

    let labelStr = option.name;
    const details = [];
    if (gender) details.push(gender);
    if (accent) details.push(accent);
    if (age) details.push(age);
    if (details.length > 0) {
      labelStr += ` (${details.join(", ")})`;
    }
    return labelStr;
  };

  const filterOptions = (
    options: Voice[],
    { inputValue }: { inputValue: string },
  ) => {
    const searchLower = inputValue.toLowerCase();
    return options.filter((voice) => {
      const name = voice.name?.toLowerCase() || "";
      const description = voice.description?.toLowerCase() || "";
      const labels = voice.labels || {};
      const gender = (labels.gender || voice.gender || "").toLowerCase();
      const accent = (labels.accent || voice.accent || "").toLowerCase();
      const age = (labels.age || voice.age || "").toLowerCase();
      const category = (voice.category || "").toLowerCase();

      return (
        name.includes(searchLower) ||
        description.includes(searchLower) ||
        gender.includes(searchLower) ||
        accent.includes(searchLower) ||
        age.includes(searchLower) ||
        category.includes(searchLower)
      );
    });
  };

  // Helper to get the value from a voice based on valueType
  const getVoiceValue = (voice: Voice | null): string | null => {
    if (!voice) return null;
    return valueType === "id"
      ? voice.id
      : voice.voiceId || voice.voice_id || null;
  };

  // Find voice by the appropriate field based on valueType
  const selectedVoice =
    voices.find((v) => {
      if (valueType === "id") {
        return v.id === value;
      }
      return v.voiceId === value || v.voice_id === value;
    }) || null;

  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", ...sx }}>
      <Autocomplete
        fullWidth
        disabled={disabled || loading}
        loading={loading}
        options={voices}
        value={selectedVoice}
        onChange={(event, newValue) => {
          onChange(getVoiceValue(newValue));
        }}
        getOptionLabel={getOptionLabel}
        filterOptions={filterOptions}
        isOptionEqualToValue={(option, val) => {
          if (valueType === "id") {
            return option.id === val?.id;
          }
          return (
            (option.voiceId || option.voice_id) ===
            (val?.voiceId || val?.voice_id)
          );
        }}
        renderOption={(props, option) => {
          const { key, ...otherProps } = props as any;
          const voiceId = option.voiceId || option.voice_id || "";
          return (
            <Box
              component="li"
              key={key}
              {...otherProps}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2">
                  {option.name}
                  {(() => {
                    const labels = option.labels || {};
                    const details = [];
                    if (labels.gender || option.gender)
                      details.push(labels.gender || option.gender);
                    if (labels.accent || option.accent)
                      details.push(labels.accent || option.accent);
                    if (details.length > 0) {
                      return ` (${details.join(", ")})`;
                    }
                    return "";
                  })()}
                </Typography>
                {option.description && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block" }}
                  >
                    {option.description.length > 80
                      ? option.description.substring(0, 80) + "..."
                      : option.description}
                  </Typography>
                )}
              </Box>
              <IconButton
                size="small"
                onClick={(e) => handleVoicePreview(voiceId, option.name, e)}
                disabled={previewLoading === voiceId}
                sx={{ ml: 1 }}
              >
                {previewLoading === voiceId ? (
                  <RefreshIcon
                    sx={{
                      animation: "spin 1s linear infinite",
                    }}
                  />
                ) : playingVoiceId === voiceId ? (
                  <StopIcon />
                ) : (
                  <VolumeUpIcon />
                )}
              </IconButton>
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder="Search voices by name, accent, gender, or description..."
            helperText={
              loading
                ? "Loading voices..."
                : helperText ||
                  `Choose from ${voices.length} high-quality voices powered by ElevenLabs`
            }
          />
        )}
      />
      {/* Test button for selected voice */}
      {showTestButton && value && selectedVoice && (
        <Button
          variant="outlined"
          size="large"
          onClick={() => {
            handleVoicePreview(
              selectedVoice.voiceId || selectedVoice.voice_id || "",
              selectedVoice.name,
            );
          }}
          disabled={
            disabled ||
            loading ||
            previewLoading === (selectedVoice.voiceId || selectedVoice.voice_id)
          }
          startIcon={
            previewLoading ===
            (selectedVoice.voiceId || selectedVoice.voice_id) ? (
              <RefreshIcon sx={{ animation: "spin 1s linear infinite" }} />
            ) : playingVoiceId ===
              (selectedVoice.voiceId || selectedVoice.voice_id) ? (
              <StopIcon />
            ) : (
              <PlayArrowIcon />
            )
          }
          sx={{ minWidth: 120, height: 56 }}
        >
          {previewLoading === (selectedVoice.voiceId || selectedVoice.voice_id)
            ? "Loading..."
            : playingVoiceId ===
                (selectedVoice.voiceId || selectedVoice.voice_id)
              ? "Stop"
              : "Test"}
        </Button>
      )}
    </Box>
  );
};

export default VoiceSelector;
